import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { getMapStyleJS } from '@/utilities/mapStyleUtils'
import { AgGridReact } from 'ag-grid-react'
import { themeBalham } from 'ag-grid-community'

import Icon from '@/components/Icon'
import Button from '@/components/Button'
import { getBBox } from '@/utilities/geospatial'
import {
  setMapCursor,
  Alert,
  setZoomingToFeature,
  bustLayerTiles,
  setSelectedFeatures,
  appendSelectedFeatures,
  setLayerType,
  removeSelectedFeatures,
} from '@/actions/index'
import useZoomToBounds from '@/utilities/Hooks/useZoomToBounds'

import { useUser } from '@/queries/hooks/use-user'
import { useUpsertUserPreference } from '@/queries/hooks/use-user-preferences'
import { useQueryClient } from '@tanstack/react-query'
import {
  useLayerProperties,
  useFeatureGeoJson,
  useUpdateFeature,
  useBulkUpdateFeatures,
  useFeatureBounds,
  useLayerRowsQuery,
  useLayerRowsFetching,
} from '@/queries/hooks/use-data-table'
import { useAnalytics } from '@/contexts/AnalyticsProvider'
import { autoSizeExpandedColumnGroups } from '@/containers/DataTable/columnGroupSizing'
import { getColumnStateForContext } from '@/lib/agGridColumnState'
import { useColumnStateCallbacks } from '@/containers/DataTable/useColumnStateCallbacks'

import TableControls from './TableControls'
import {
  toDateObject,
  formatDateForDisplay,
  toApiFormat,
  processServerDateValue,
} from './dateUtils'
import scss from './DataTable.scss'
import { SELECTED_FEATURES } from '../../data/Layers/Auxiliary/SelectedFeatures'
import {
  normalizeFilterModel,
  stripFilterMeta,
  isBlankValue,
  MAM_AREA_PATTERN,
  MAM_PERIMETER_PATTERN,
  getMamConversionFactor,
  convertFilterModelMamToSi,
} from '@/containers/DataTable/filterUtils'
import {
  getLayerConfigUsingLayerId,
  getCommonLayerConfigUsingLayerId,
} from '@/utilities/dataConfigSelectors'

const SELECTED_FEATURES_LAYER = SELECTED_FEATURES.layersArray[0].layer

// ========== AG-GRID V33 THEME CONFIGURATION ========== //
/**
 * Theme Configuration for AG-Grid v33
 * Based on original styling requirements from:
 * - public/ag-grid.css
 * - public/ag-grid-enterprise-overrides.css
 * - src/containers/DataTable/DataTable.scss
 */
const DATA_TABLE_THEME_V2 = themeBalham.withParams({
  // Core colors (exact from original styling)
  accentColor: '#1a2937',
  backgroundColor: '#ffffff',
  headerBackgroundColor: '#dce6ef',
  headerTextColor: '#1a2937',

  // Typography (exact from original)
  fontSize: '10px',
  fontFamily: 'Montserrat, sans-serif',

  // Layout (exact from original)
  rowHeight: 22,
  borderRadius: 2,

  // Side panel (exact from original styling)
  sidePanelBackgroundColor: '#dce6ef',
  toolPanelBackgroundColor: '#dce6ef',

  // Filter panel (exact from original styling)
  filterToolPanelGroupTitleBarBackgroundColor: '#e4e5e7',

  // Selection and checkboxes (exact from original styling)
  checkboxCheckedBackgroundColor: '#1a2937',
  checkboxIndeterminateBackgroundColor: '#1b2e40',
  selectedRowBackgroundColor: 'rgba(26, 41, 55, 0.1)',

  // Range selection (exact from original styling)
  rangeSelectionBorderColor: '#1a2937',
  rangeFillColor: 'rgba(26, 41, 55, 0.1)',

  // Border colors (exact from original styling)
  borderColor: '#979797',
})

// Helper function to detect coordinate columns with regex matching
const isCoordinateField = fieldName => {
  if (!fieldName) return false
  const lowerFieldName = fieldName.toLowerCase()

  // MAM coordinate fields (Point geometry lat/lon) — full names and common abbreviations.
  if (
    lowerFieldName === 'mam_latitude' ||
    lowerFieldName === 'mam_longitude' ||
    lowerFieldName === 'mam_lat' ||
    lowerFieldName === 'mam_lon' ||
    lowerFieldName === 'mam_lng'
  ) {
    return true
  }

  // Coordinate patterns (lat/lon only)
  const latitudePattern = /\b(lat|latitude)\b/
  const longitudePattern = /\b(lon|lng|long|longitude)\b/
  const containsLatitude = /(^|\W)(lat|latitude)($|\W)/
  const containsLongitude = /(^|\W)(lon|lng|long|longitude)($|\W)/

  // MAM-prefixed lat/lon variants not caught by the direct check above
  const mamCoordinatePattern = /^mam[_\s](lat|latitude|lon|lng|longitude)/

  return (
    latitudePattern.test(lowerFieldName) ||
    longitudePattern.test(lowerFieldName) ||
    containsLatitude.test(lowerFieldName) ||
    containsLongitude.test(lowerFieldName) ||
    mamCoordinatePattern.test(lowerFieldName)
  )
}

const appendUniqueFilterOptions = (target, options = []) => {
  if (!Array.isArray(target)) return
  options.forEach(option => {
    if (!target.includes(option)) {
      target.push(option)
    }
  })
}

const buildDropdownFilterValues = values => {
  const uniqueValues = Array.isArray(values) ? Array.from(new Set(values)) : []

  if (!uniqueValues.some(value => value === null)) {
    uniqueValues.push(null)
  }

  if (!uniqueValues.some(value => value === '')) {
    uniqueValues.push('')
  }

  return uniqueValues
}

/**
 * Extracts tab configuration from layer metadata
 * @param {Object} dataConfig - The data configuration from Redux
 * @param {string} layerId - The ID of the layer
 * @param {Object} mapStyle - The map style from Redux
 * @returns {Array} Array of tab objects with structure: { id, title, itemsIds, category }
 */
const getLayerTabConfiguration = (dataConfig, layerId, mapStyle) => {
  if (!dataConfig || !layerId) return null

  try {
    const style = mapStyle?.toJS ? mapStyle.toJS() : mapStyle
    const layer = style?.layers?.find(l => l.id === layerId)
    const isCommonLayer = layer?.metadata?.commonLayer

    let configLayer
    if (isCommonLayer) {
      configLayer = getCommonLayerConfigUsingLayerId(dataConfig, layerId)
    } else {
      configLayer = getLayerConfigUsingLayerId(dataConfig, layerId)
    }

    if (
      configLayer &&
      configLayer.layersArray &&
      configLayer.layersArray[0] &&
      configLayer.layersArray[0].layer &&
      configLayer.layersArray[0].layer.metadata &&
      configLayer.layersArray[0].layer.metadata.popup &&
      configLayer.layersArray[0].layer.metadata.popup.tabs
    ) {
      const { tabs } = configLayer.layersArray[0].layer.metadata.popup

      const filteredTabs = Object.values(tabs).filter(
        tab =>
          tab &&
          tab.title !== 'Unassigned' &&
          (!tab.category || tab.category.toLowerCase() === 'attributes')
      )

      return filteredTabs.length > 0 ? filteredTabs : null
    }
  } catch {
    // Gracefully degrade — return null so table renders flat columns
  }

  return null
}

/**
 * Helper to extract field names from flat or grouped column definitions
 * Recursively processes column groups to collect all field names
 * @param {Array} cols - Column definitions (can be flat or grouped)
 * @returns {Array} Array of field names (excluding 'select' and 'zoomTo')
 */
const getAllFields = cols => {
  const fields = []
  if (!cols) return fields

  cols.forEach(col => {
    if (col.children) {
      // This is a column group, get fields from children
      fields.push(...getAllFields(col.children))
    } else if (col.field && col.field !== 'select' && col.field !== 'zoomTo') {
      // This is a regular column with a field
      fields.push(col.field)
    }
  })
  return fields
}

/**
 * Build a flat ordered list of property column defs that mirrors the popup
 * tab order. Tabs are walked in their configured order; within each tab,
 * properties follow `itemsIds`. Any properties not assigned to a tab are
 * appended at the end so nothing is silently dropped.
 *
 * Used when the user has the "show tabs in data table" preference disabled
 * (the default per MAM-775) — columns are flat but their order matches the
 * feature attribute popup that the user already knows.
 *
 * @param {Array|null} tabsConfig - Tabs from getLayerTabConfiguration
 * @param {Array} layerProperties - Raw layer property definitions
 * @param {Object} propertyIdToColumn - Map of property id (string) → built columnDef
 * @returns {Array} Ordered array of column defs (no AG Grid groups)
 */
const buildPopupOrderedFlatColumns = (
  tabsConfig,
  layerProperties,
  propertyIdToColumn
) => {
  const ordered = []
  const usedPropertyIds = new Set()

  if (Array.isArray(tabsConfig)) {
    tabsConfig.forEach(tab => {
      if (!tab?.itemsIds || tab.itemsIds.length === 0) return
      tab.itemsIds.forEach(propertyId => {
        const propertyKey = String(propertyId)
        const column = propertyIdToColumn[propertyKey]
        if (column && !usedPropertyIds.has(propertyKey)) {
          ordered.push(column)
          usedPropertyIds.add(propertyKey)
        }
      })
    })
  }

  // Append any properties not referenced by a tab (or all properties when no
  // tab config exists) in their natural layerProperties order so the table
  // never silently drops a column.
  layerProperties.forEach(col => {
    if (!col.id) return
    const propertyKey = String(col.id)
    if (usedPropertyIds.has(propertyKey)) return
    const column = propertyIdToColumn[propertyKey]
    if (column) {
      ordered.push(column)
      usedPropertyIds.add(propertyKey)
    }
  })

  return ordered
}

const ZoomToFeatureButton = ({ data, onZoomToFeature }) => {
  return (
    <Button
      onClick={() => onZoomToFeature(data.mamid)}
      variant='tertiary'
      size='sm'
      iconProps={{ icon: ['fal', 'arrows-alt'] }}
      className={scss['zoom-to-feature-button']}
      data-testid='zoom-to-feature-button'
    />
  )
}

ZoomToFeatureButton.displayName = 'ZoomToFeatureButton'

const CSV_NEWLINE = '\r\n'

const buildRowDataFromServer = (
  rowData = [],
  columnDefs = [],
  propertyLookupObj = {}
) => {
  const fieldsToProcess = getAllFields(columnDefs)
  const nextOriginalData = {}
  const processedRows = rowData.map(row => {
    const rowObj = {
      zoomTo: <Icon icon='file' />,
    }

    fieldsToProcess.forEach(field => {
      const fieldType =
        field === 'mamid' ? 'mamid' : propertyLookupObj[field]?.type

      if (fieldType === 'date') {
        rowObj[field] = processServerDateValue(
          row[field],
          propertyLookupObj[field]
        )
        return
      }

      rowObj[field] = row[field]

      if (
        (rowObj[field] === null || rowObj[field] === '') &&
        propertyLookupObj[field]?.default
      ) {
        rowObj[field] = propertyLookupObj[field].default
      }
    })

    if (rowObj.mamid !== undefined) {
      nextOriginalData[String(rowObj.mamid)] = { ...rowObj }
    }

    return rowObj
  })

  return { processedRows, nextOriginalData }
}

const csvEscapeValue = value => {
  if (value === null || value === undefined) return ''
  const stringValue = String(value)
  const needsQuotes = /[",\n]/.test(stringValue)
  const escaped = stringValue.replace(/"/g, '""')
  return needsQuotes ? `"${escaped}"` : escaped
}

const downloadCsv = (content, filename) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  link.click()
  URL.revokeObjectURL(url)
}

/**
 * DataTable Component - Enhanced AG-Grid data table with save/cancel functionality
 *
 * @param {Object} props - Component props
 * @param {number} props.height - Table height in pixels
 * @param {Function} props.onClose - Callback when table is closed
 * @param {Object} ref - Forwarded ref for imperative methods
 */
const DataTable = forwardRef(({ height, onClose }, ref) => {
  const { data: user } = useUser() // Required for authentication context and date preferences
  const { mutate: upsertPreference } = useUpsertUserPreference()
  const { trackEvent } = useAnalytics()
  const dispatch = useDispatch()
  // Store handle for async callbacks that need the *latest* mapStyle. The
  // `useSelector` snapshot below is fine for render-time reads, but async
  // mutation `onSuccess` closures capture a stale snapshot — see
  // `updateSelectLayer` / `removeSelectedFeature` below.
  const store = useStore()
  const queryClient = useQueryClient()
  const dataTable = useSelector(state => state.dataTable)
  const mapStyle = useSelector(state => state.mapStyle)
  const dataConfig = useSelector(state => state.updateDataConfig)

  // Basic state
  const [propertyLookupObj, setPropertyLookupObj] = useState({})
  const [gridApi, setGridApi] = useState(null)
  const [rowCount, setRowCount] = useState(null)
  const [filterChecked, setFilterChecked] = useState(
    Boolean(dataTable?.applyFilterToMap)
  )
  const [currentFilter, setCurrentFilter] = useState(null)
  const [tableControlKey, setTableControlKey] = useState(1)

  // Edit state management
  const [editedCells, setEditedCells] = useState({}) // Format: {featureId: {field: {newValue, oldValue}}}
  const [originalData, setOriginalData] = useState({}) // Store original row data for reset functionality
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  const isZoomToFeatureRef = useRef(false)
  const { layerId, layerLabel } = dataTable || {}

  // Column persistence mode state - initialized after layerId is available
  const [persistenceMode, _setPersistenceMode] = useState(() => {
    // Load persistence mode from localStorage or default to 'personal'
    if (typeof window !== 'undefined' && window.localStorage && layerId) {
      try {
        const saved = localStorage.getItem(
          `dataTable_persistenceMode_${layerId}`
        )
        return saved === 'layer' ? 'layer' : 'personal'
      } catch {
        return 'personal'
      }
    }
    return 'personal'
  })

  const [tabsEnabled, setTabsEnabled] = useState(() =>
    Boolean(user?.preferences?.['data_table.show_tabs']?.[0]?.value)
  )

  // Lifted so `handleToggleTabs` can short-circuit when the active layer has
  // no popup tabs configured. The grouped path in `columnDefs` reads the
  // same memoized value.
  const tabsConfig = useMemo(
    () => getLayerTabConfiguration(dataConfig, layerId, mapStyle),
    [dataConfig, layerId, mapStyle]
  )
  const layerHasTabs = Array.isArray(tabsConfig) && tabsConfig.length > 0

  const handleToggleTabs = useCallback(() => {
    // No popup tabs on this layer → flipping the toggle would have no visible
    // effect (grouped mode falls through to the flat list). Surface a toast
    // pointing the user at where to configure tabs, and leave the preference
    // alone so it still works on other layers where tabs are set up.
    if (!layerHasTabs) {
      dispatch(
        Alert({
          info: 'This layer has no attribute tabs configured. Set up popup tabs in the layer settings to use column grouping.',
        })
      )
      trackEvent('data_table_tabs_toggle_blocked', {
        tool_name: 'data_table',
        layer_id: layerId,
        layer_label: layerLabel,
        reason: 'no_tabs_configured',
      })
      return
    }

    setTabsEnabled(prev => {
      const nextValue = !prev
      upsertPreference({
        key: 'data_table.show_tabs',
        name: 'show_tabs',
        value: nextValue,
      })
      trackEvent('data_table_tabs_toggled', {
        tool_name: 'data_table',
        layer_id: layerId,
        layer_label: layerLabel,
        new_state: nextValue ? 'enabled' : 'disabled',
      })
      return nextValue
    })
  }, [
    layerHasTabs,
    dispatch,
    upsertPreference,
    trackEvent,
    layerId,
    layerLabel,
  ])

  const { zoomToBounds } = useZoomToBounds()

  const cacheBlockSize = 100

  const localeTextFunc = useCallback((key, defaultValue) => {
    if (key === 'filterBlank') return 'Is Empty'
    if (key === 'filterNotBlank') return 'Is Not Empty'
    if (key === 'blanks' || key === 'setFilterBlanks') return 'Empty'
    return defaultValue
  }, [])

  const updateNormalizedFilterState = useCallback(filterModel => {
    const normalized = normalizeFilterModel(filterModel)
    setCurrentFilter(normalized)
    return normalized
  }, [])

  const {
    data: layerProperties,
    isLoading: propertiesLoading,
    error: propertiesError,
  } = useLayerProperties(layerId)

  const featureGeoJsonQuery = useFeatureGeoJson()
  const featureBoundsQuery = useFeatureBounds()
  const updateFeatureMutation = useUpdateFeature()
  const bulkUpdateMutation = useBulkUpdateFeatures()
  const { fetchRows } = useLayerRowsQuery()
  const layerRowsFetching = useLayerRowsFetching(layerId)

  useImperativeHandle(ref, () => ({
    exportData: () => {
      if (gridApi) {
        return gridApi.getDataAsCsv()
      }
      return null
    },
    refreshData: () => {
      if (gridApi) {
        gridApi.refreshServerSide()
      }
    },
  }))

  /**
   * Default column definition with cell styling for edited cells
   * AG-Grid v33: cellClass and cellStyle are now set at column level, not grid level
   */
  const defaultColDef = useMemo(
    () => ({
      cellClass: params => {
        const { data, colDef } = params
        if (!data || !colDef || !colDef.field) return ''

        const featureId = data.mamid
        const { field } = colDef

        // Check if this cell has been edited
        if (editedCells[featureId] && editedCells[featureId][field]) {
          return 'ag-cell-edited'
        }

        return ''
      },
      cellStyle: params => {
        const { data, colDef } = params
        if (!data || !colDef || !colDef.field) return {}

        const featureId = data.mamid
        const { field } = colDef

        // Check if this cell has been edited
        if (editedCells[featureId] && editedCells[featureId][field]) {
          return {
            backgroundColor: '#FCD857',
            border: '1px solid #e6c23d',
            boxSizing: 'border-box',
          }
        }

        return {}
      },
    }),
    [editedCells]
  )

  /**
   * Converts normalized values back to API format using consolidated utility
   */
  const convertToApiFormat = useCallback((value, fieldType) => {
    if (fieldType === 'date') {
      return toApiFormat(value)
    }
    return value
  }, [])

  /**
   * Handles saving all pending changes to the server
   */
  const handleSaveChanges = useCallback(() => {
    const updates = []

    // Convert editedCells to bulk update format
    Object.entries(editedCells).forEach(([featureId, fields]) => {
      const properties = {}
      Object.entries(fields).forEach(([field, { newValue }]) => {
        const fieldType = propertyLookupObj[field]?.type || 'text'
        properties[field] = convertToApiFormat(newValue, fieldType)
      })
      updates.push({ featureId, properties })
    })

    if (updates.length === 0) return

    // Capture start time for duration tracking
    const startTime = Date.now()

    // Use single or bulk update based on number of changes
    if (updates.length === 1) {
      const { featureId, properties } = updates[0]
      const fieldsModified = Object.keys(properties)

      updateFeatureMutation.mutate(
        {
          layerId,
          featureId,
          properties,
        },
        {
          onSuccess: () => {
            // Track successful single feature update
            const operationDuration = Date.now() - startTime
            trackEvent('datatable_save_successful', {
              update_type: 'single',
              features_updated: 1,
              fields_modified: fieldsModified,
              field_count: fieldsModified.length,
              operation_duration_ms: operationDuration,
              layer_id: layerId,
            })

            const fieldNames = Object.keys(properties).join(', ')
            dispatch(
              Alert({
                success: `Successfully updated ${fieldNames} for feature ${featureId}`,
              })
            )
            // Invalidate feature properties so AttributeTab refreshes
            queryClient.invalidateQueries({
              queryKey: ['feature-properties', layerId],
            })
            // Bust tiles so data-driven styles reflect immediately
            dispatch(bustLayerTiles(layerId))
            // Clear edited state and update original data
            setEditedCells({})
            setOriginalData(prev => {
              const updated = { ...prev }
              if (updated[featureId]) {
                Object.entries(editedCells[featureId] || {}).forEach(
                  ([field, { newValue }]) => {
                    updated[featureId][field] = newValue
                  }
                )
              }
              return updated
            })
            // Update row data directly to prevent disappearing rows
            if (gridApi) {
              gridApi.forEachNode(rowNode => {
                if (String(rowNode.data.mamid) === featureId) {
                  const updatedData = { ...rowNode.data }
                  Object.entries(editedCells[featureId] || {}).forEach(
                    ([field, { newValue }]) => {
                      updatedData[field] = newValue
                    }
                  )
                  rowNode.setData(updatedData)
                }
              })
            }
          },
          onError: error => {
            // Track failed single feature update
            const operationDuration = Date.now() - startTime
            trackEvent('datatable_save_failed', {
              update_type: 'single',
              features_attempted: 1,
              fields_attempted: fieldsModified,
              field_count: fieldsModified.length,
              operation_duration_ms: operationDuration,
              error_message: error.message || 'Unknown error',
              layer_id: layerId,
              feature_id: featureId,
            })

            dispatch(
              Alert({
                error: `Unable to update feature ${featureId}: ${error.message || 'Unknown error'}`,
              })
            )
          },
        }
      )
    } else {
      // Bulk update for multiple changes
      bulkUpdateMutation.mutate(
        {
          layerId,
          updates,
        },
        {
          onSuccess: results => {
            const successful = results.filter(r => r.success)
            const failed = results.filter(r => !r.success)

            // Get successful feature IDs (normalize to strings for consistency with grid/editedCells)
            const successfulFeatureIds = successful.map(s =>
              String(s.featureId)
            )

            // Compute fields_modified and field_count from only successful updates
            const successfulUpdates = updates.filter(u =>
              successfulFeatureIds.includes(String(u.featureId))
            )
            const successfulFieldsModified = [
              ...new Set(
                successfulUpdates.flatMap(u => Object.keys(u.properties))
              ),
            ]
            const successfulFieldCount = successfulUpdates.reduce(
              (sum, u) => sum + Object.keys(u.properties).length,
              0
            )

            // Track successful bulk update
            const operationDuration = Date.now() - startTime
            trackEvent('datatable_save_successful', {
              update_type: 'bulk',
              features_updated: successful.length,
              fields_modified: successfulFieldsModified,
              field_count: successfulFieldCount,
              operation_duration_ms: operationDuration,
              layer_id: layerId,
              has_partial_failures: failed.length > 0,
              failed_count: failed.length,
            })

            if (successful.length > 0) {
              dispatch(
                Alert({
                  success: `Successfully updated ${successful.length} feature${successful.length > 1 ? 's' : ''}`,
                })
              )
              // Invalidate feature properties for this layer (and active popups)
              queryClient.invalidateQueries({
                queryKey: ['feature-properties', layerId],
              })
            }

            if (failed.length > 0) {
              const errorMessages = failed
                .map(f => `Feature ${f.featureId}: ${f.error}`)
                .join('; ')
              dispatch(
                Alert({
                  error: `${failed.length} update${failed.length > 1 ? 's' : ''} failed: ${errorMessages}`,
                })
              )

              // Only clear edited state for successful updates
              setEditedCells(prev => {
                const updated = { ...prev }
                successfulFeatureIds.forEach(featureId => {
                  delete updated[featureId]
                })
                return updated
              })
            } else {
              // All updates successful, clear all edits
              setEditedCells({})
            }

            // Update original data and row display for successful saves
            setOriginalData(prev => {
              const updated = { ...prev }
              successfulFeatureIds.forEach(featureId => {
                if (updated[featureId] && editedCells[featureId]) {
                  Object.entries(editedCells[featureId]).forEach(
                    ([field, { newValue }]) => {
                      updated[featureId][field] = newValue
                    }
                  )
                }
              })
              return updated
            })

            // Update row data directly to prevent disappearing rows
            if (gridApi) {
              gridApi.forEachNode(rowNode => {
                const featureId = String(rowNode.data.mamid)
                if (
                  successfulFeatureIds.includes(featureId) &&
                  editedCells[featureId]
                ) {
                  const updatedData = { ...rowNode.data }
                  Object.entries(editedCells[featureId]).forEach(
                    ([field, { newValue }]) => {
                      updatedData[field] = newValue
                    }
                  )
                  rowNode.setData(updatedData)
                }
              })
            }

            // Bust tiles so data-driven styles reflect immediately
            dispatch(bustLayerTiles(layerId))
          },
          onError: error => {
            // Track completely failed bulk update
            // Compute all attempted fields since entire mutation failed
            const allFieldsAttempted = [
              ...new Set(updates.flatMap(u => Object.keys(u.properties))),
            ]
            const totalFieldsAttempted = updates.reduce(
              (sum, u) => sum + Object.keys(u.properties).length,
              0
            )

            const operationDuration = Date.now() - startTime
            trackEvent('datatable_save_failed', {
              update_type: 'bulk',
              features_attempted: updates.length,
              fields_attempted: allFieldsAttempted,
              field_count: totalFieldsAttempted,
              operation_duration_ms: operationDuration,
              error_message: error.message || 'Unknown error',
              layer_id: layerId,
            })

            dispatch(
              Alert({
                error: `Bulk update failed: ${error.message || 'Unknown error'}`,
              })
            )
          },
        }
      )
    }
  }, [
    editedCells,
    layerId,
    updateFeatureMutation,
    bulkUpdateMutation,
    dispatch,
    gridApi,
    queryClient,
    propertyLookupObj,
    convertToApiFormat,
    trackEvent,
  ])

  /**
   * Handles canceling all pending changes and reverting to original values
   */
  const handleCancelChanges = useCallback(() => {
    // Track cancellation before reverting changes
    const featureIds = Object.keys(editedCells)
    const totalEdits = Object.values(editedCells).reduce(
      (sum, fields) => sum + Object.keys(fields).length,
      0
    )

    trackEvent('datatable_changes_cancelled', {
      feature_count: featureIds.length,
      total_edits_cancelled: totalEdits,
      feature_ids: featureIds,
      layer_id: layerId,
    })

    // Revert all edited cells to their original values
    Object.entries(editedCells).forEach(([featureId, fields]) => {
      Object.entries(fields).forEach(([field, { oldValue }]) => {
        // Find the row node and update its data
        gridApi.forEachNode(rowNode => {
          if (String(rowNode.data.mamid) === featureId) {
            const updatedData = { ...rowNode.data }
            updatedData[field] = oldValue
            rowNode.setData(updatedData)
          }
        })
      })
    })

    // Clear edited state
    setEditedCells({})
  }, [editedCells, gridApi, trackEvent, layerId])

  const dataTableTheme = useMemo(() => DATA_TABLE_THEME_V2, [])

  // Create property lookup object separately (no side effects in useMemo)
  const derivedPropertyLookupObj = useMemo(() => {
    if (!layerProperties || !Array.isArray(layerProperties)) {
      return {}
    }
    const pLookupObj = {}
    layerProperties.forEach(property => {
      pLookupObj[property.key] = property
    })
    return pLookupObj
  }, [layerProperties])

  // Sync property lookup to state for use in callbacks
  useEffect(() => {
    setPropertyLookupObj(derivedPropertyLookupObj)
  }, [derivedPropertyLookupObj])

  const columnDefs = useMemo(() => {
    if (!layerProperties || !Array.isArray(layerProperties)) {
      return null
    }

    // Build column definitions for each property
    const buildColumnDef = col => {
      let filterType
      let editType
      let dropdownOptions

      const filterParams = {
        filterOptions: ['equals', 'notEqual'],
      }

      // Set cell editor based on column type
      if (col.type === 'date') {
        editType = 'agDateCellEditor'
        filterType = 'agDateColumnFilter'
        // Add the specific date filter options requested: before, after, between
        filterParams.filterOptions = [
          'equals',
          'lessThan', // before
          'greaterThan', // after
          'inRange', // between
        ]
        filterParams.browserDatePicker = true
        filterParams.minValidYear = 1900
        filterParams.maxValidYear = 2100
      } else if (col.type === 'currency') {
        editType = 'agNumberCellEditor'
      } else if (col.type === 'longtext') {
        editType = 'agLargeTextCellEditor'
      } else if (col.type === 'dropdown') {
        editType = 'agSelectCellEditor'
        // Use property.value as dropdown options (same as popup AttributeTab)
        if (col.value && Array.isArray(col.value) && col.value.length > 0) {
          dropdownOptions = col.value.filter(
            value => value !== null && value !== ''
          )
        }
      }

      const key = col.key.toLowerCase()
      const name = (col.name || '').toLowerCase()

      // Detect MAM calculated geometry fields early — needed for filter type determination
      // and column config (sortable, editable, header suffix, value formatter).
      const isMamColumn = key.startsWith('mam')
      const isMamAreaField = MAM_AREA_PATTERN.test(key)
      const isMamPerimeterField = MAM_PERIMETER_PATTERN.test(key)

      const latitudePattern = /\b(lat|latitude)\b/
      const longitudePattern = /\b(lon|lng|longitude)\b/
      const isLatitude = latitudePattern.test(key) || latitudePattern.test(name)
      const isLongitude =
        longitudePattern.test(key) || longitudePattern.test(name)
      const isCoordinateColumn = isLatitude || isLongitude

      // MAM area/perimeter/length fields always get number filter so the
      // SI↔imperial filter conversion works regardless of what col.type the
      // server returns for intrinsic geometry fields.
      if (
        col.type === 'number' ||
        isCoordinateColumn ||
        isMamAreaField ||
        isMamPerimeterField
      ) {
        filterType = 'agNumberColumnFilter'
        editType = 'agNumberCellEditor'
        appendUniqueFilterOptions(filterParams.filterOptions, [
          'lessThan',
          'lessThanOrEqual',
          'greaterThan',
          'greaterThanOrEqual',
          'inRange',
          'blank',
          'notBlank',
        ])
      } else if (col.type === 'dropdown') {
        filterType = 'agSetColumnFilter'
        const dropdownFilterValues = buildDropdownFilterValues(col.value)
        filterParams.values = dropdownFilterValues
        filterParams.valueFormatter = params =>
          isBlankValue(params.value) ? 'Empty' : params.value
      } else if (col.type === 'date') {
        // Date filter type already set above - don't override
      } else {
        filterType = 'agTextColumnFilter'
        appendUniqueFilterOptions(filterParams.filterOptions, [
          'contains',
          'notContains',
          'blank',
          'notBlank',
        ])
      }

      // Append unit suffix to the header so users know what unit they're reading.
      let headerName = col.name
      if (isMamAreaField) headerName = `${col.name} (ft²)`
      else if (isMamPerimeterField) headerName = `${col.name} (ft)`

      const isNonSortableColumn =
        isCoordinateField(col.key) || isMamAreaField || isMamPerimeterField
      const columnDef = {
        headerName,
        field: col.key,
        colId: col.key,
        sortable: !isNonSortableColumn,
        editable: col.type !== 'document' && !isMamColumn,
        resizable: true,
        filter: isNonSortableColumn ? false : filterType,
        filterParams: isNonSortableColumn ? undefined : filterParams,
        enablePivot: true,
        enableRowGroup: true,
        enableValue: true,
        cellEditor: editType,
        suppressFillHandle: isMamColumn,
        cellDataType: col.type === 'date' ? 'date' : undefined,
      }

      // MAM calculated fields: convert from SI (m / m²) to imperial (ft / ft²),
      // rounded-sm to 3 decimal places. Uses the same factor as export and filters.
      if (isMamAreaField || isMamPerimeterField) {
        columnDef.valueFormatter = params => {
          if (params.value == null || params.value === '') return ''
          const factor = getMamConversionFactor(col.key)
          const num = parseFloat(params.value)
          if (factor == null || Number.isNaN(num)) return params.value
          return (num * factor).toFixed(3)
        }
      }

      // Add cell editor parameters based on type
      if (dropdownOptions && dropdownOptions.length > 0) {
        columnDef.cellEditorParams = { values: dropdownOptions }
      } else if (editType === 'agNumberCellEditor') {
        columnDef.cellEditorParams = {
          // Only allow numbers and decimal points
          preventDefaultOnEnter: true,
          useKeyPress: true,
          allowedCharPattern: '\\d|\\.|-',
        }
      }

      // Add currency formatting
      if (col.type === 'currency') {
        columnDef.valueFormatter = params => {
          if (params.value == null || params.value === '') return ''
          const num = parseFloat(params.value)
          if (Number.isNaN(num)) return params.value
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(num)
        }

        columnDef.valueSetter = params => {
          // Remove currency formatting when setting value
          let value = params.newValue
          if (typeof value === 'string') {
            value = value.replace(/[$,]/g, '')
          }
          const num = parseFloat(value)
          params.data[params.colDef.field] = Number.isNaN(num) ? null : num
          return true
        }
      }

      // Add date picker configuration using agDateStringCellEditor
      if (col.type === 'date') {
        // Use agDateCellEditor with Date objects for better control
        editType = 'agDateCellEditor'

        // Display dates using user preferences and consolidated utility
        columnDef.valueFormatter = params => {
          return formatDateForDisplay(params.value, user)
        }

        // Simple value setter - store Date objects directly in grid data
        columnDef.valueSetter = params => {
          if (!params.newValue) {
            params.data[params.colDef.field] = null
            return true
          }

          // Store Date objects directly from agDateCellEditor
          params.data[params.colDef.field] = params.newValue
          return true
        }
      }

      // Add multiline text configuration
      if (col.type === 'longtext') {
        columnDef.autoHeight = true
        columnDef.cellClass =
          'whitespace-pre-wrap wrap-break-word leading-6 px-1 py-2'

        // Simple cell editor params - fixed height of two rows
        columnDef.cellEditorParams = {
          maxLength: 5000,
          rows: 2,
          cols: 50,
        }
      }

      if (isCoordinateColumn) {
        columnDef.comparator = (valueA, valueB) => {
          const numA = parseFloat(valueA)
          const numB = parseFloat(valueB)

          if (Number.isNaN(numA) && Number.isNaN(numB)) return 0
          if (Number.isNaN(numA)) return 1
          if (Number.isNaN(numB)) return -1

          return numA - numB
        }
      }

      return columnDef
    }

    // Build property columns and organize into groups.
    // Regular layer properties have an `id` and are referenced by popup tabs
    // via `itemsIds`. Intrinsic geometry fields (e.g. MAM Area, MAM Perimeter)
    // are returned by the API (via `includeIntrinsicGeom: true`) without an
    // `id` — they are never assigned to a popup tab, so they get rendered in
    // their own "Geometry" group (grouped mode) or appended to the end of
    // the flat column list (flat mode).
    const propertyIdToColumn = {}
    const intrinsicColumns = []

    layerProperties.forEach(col => {
      if (!col.key) return

      const columnDef = buildColumnDef(col)
      if (col.id) {
        propertyIdToColumn[col.id.toString()] = columnDef
      } else {
        intrinsicColumns.push(columnDef)
      }
    })

    // Add fixed columns (mamid and zoomTo) at the beginning
    const finalColumns = []

    finalColumns.push({
      headerName: '',
      field: 'zoomTo',
      cellRenderer: 'zoomToFeatureButton',
      hide: false,
      resizable: false,
      pinned: 'left',
      width: 40,
      suppressFillHandle: true,
    })

    finalColumns.push({
      headerName: 'mamid',
      field: 'mamid',
      colId: 'mamid',
      sortable: true,
      editable: false,
      resizable: true,
      filter: 'agNumberColumnFilter',
      filterParams: {
        filterOptions: [
          'equals',
          'notEqual',
          'lessThan',
          'lessThanOrEqual',
          'greaterThan',
          'greaterThanOrEqual',
          'inRange',
          'blank',
          'notBlank',
        ],
      },
      hide: false,
      pinned: 'left',
      width: 100,
      suppressFillHandle: true,
    })

    // Helper: apply standard grouped-column styling (flex sizing,
    // columnGroupShow rules so the first child anchors the caret and the
    // rest reveal when the group is open).
    const applyGroupedColumnStyling = columns => {
      columns.forEach((column, index) => {
        column.columnGroupShow = index === 0 ? null : 'open'
        column.flex = 1
        column.minWidth = 100
      })
    }

    // Tabs preference ON + tab config available → render AG Grid column groups
    // matching the popup tabs (with an "Other" group for unassigned properties
    // and a "Geometry" group for intrinsic geometry fields). Per MAM-775 this
    // is opt-in via the toolbar.
    if (tabsEnabled && tabsConfig && tabsConfig.length > 0) {
      const columnGroups = []
      const usedPropertyIds = new Set()

      tabsConfig.forEach(tab => {
        if (!tab.itemsIds || tab.itemsIds.length === 0) return

        const childColumns = []
        tab.itemsIds.forEach(propertyId => {
          const propertyKey = String(propertyId)
          const column = propertyIdToColumn[propertyKey]
          if (column) {
            childColumns.push(column)
            usedPropertyIds.add(propertyKey)
          }
        })

        if (childColumns.length > 0) {
          applyGroupedColumnStyling(childColumns)
          columnGroups.push({
            groupId: tab.id,
            headerName: tab.title,
            children: childColumns,
            marryChildren: true,
            openByDefault: true,
          })
        }
      })

      // Properties with ids that aren't referenced by any popup tab → "Hidden".
      // These are effectively the layer's hidden attributes, so the group
      // renders collapsed by default — only the first child (the caret anchor)
      // is visible until the user expands the group.
      const unassignedColumns = layerProperties
        .filter(col => col.id && !usedPropertyIds.has(String(col.id)))
        .map(col => propertyIdToColumn[String(col.id)])
        .filter(Boolean)

      if (unassignedColumns.length > 0) {
        applyGroupedColumnStyling(unassignedColumns)
        columnGroups.push({
          groupId: 'hidden_group',
          headerName: 'Hidden',
          children: unassignedColumns,
          marryChildren: true,
          openByDefault: false,
        })
      }

      // Intrinsic geometry fields (MAM Area, MAM Perimeter, etc.) live in
      // their own group at the end since they're never part of a popup tab.
      if (intrinsicColumns.length > 0) {
        applyGroupedColumnStyling(intrinsicColumns)
        columnGroups.push({
          groupId: 'geometry_group',
          headerName: 'Geometry',
          children: intrinsicColumns,
          marryChildren: true,
          openByDefault: true,
        })
      }

      finalColumns.push(...columnGroups)
    } else {
      // Default path (tabs disabled OR no tab config). Render a flat column
      // list ordered to match the feature attribute popup so users see the
      // shape they already know. Manual reordering still wins via the
      // localStorage column-state restore below. Intrinsic geometry fields
      // are appended at the end since they have no popup tab assignment.
      finalColumns.push(
        ...buildPopupOrderedFlatColumns(
          tabsConfig,
          layerProperties,
          propertyIdToColumn
        ),
        ...intrinsicColumns
      )
    }

    return finalColumns
  }, [layerProperties, user, tabsConfig, tabsEnabled])

  // Stable structural key derived from columnDefs. The `columnDefs` array gets
  // rebuilt on every `mapStyle` dispatch (row selection updates `mapStyle`,
  // which invalidates `tabsConfig`'s memo, which invalidates `columnDefs`),
  // but the actual displayed columns rarely change. Using this key as an
  // effect dependency lets us skip the AG Grid column-state restore and
  // column-group analytics re-run when only the array *reference* changed —
  // avoiding the visible column jump on every cell click (MAM-810).
  const columnStructureKey = useMemo(() => {
    if (!Array.isArray(columnDefs)) return null
    const tokens = []
    const walk = cols => {
      cols.forEach(col => {
        if (Array.isArray(col?.children)) {
          tokens.push(`group:${col.groupId || col.headerName || ''}`)
          walk(col.children)
        } else {
          tokens.push(col?.colId || col?.field || '')
        }
      })
    }
    walk(columnDefs)
    return tokens.join('|')
  }, [columnDefs])

  const columnStateContextKey = useMemo(() => {
    if (!layerId || !columnStructureKey) return null
    return JSON.stringify([layerId, persistenceMode, columnStructureKey])
  }, [layerId, persistenceMode, columnStructureKey])

  const { columnStateLifecycle, saveColumnState, onInitialModelUpdated } =
    useColumnStateCallbacks(
      gridApi,
      columnStateContextKey,
      layerId ? `dataTable_columnState_${persistenceMode}_${layerId}` : null
    )

  const lastTrackedColumnGroupsRef = useRef('')
  useEffect(() => {
    if (!layerId || !Array.isArray(columnDefs)) return

    const groupedColumns = columnDefs.filter(col =>
      Array.isArray(col?.children)
    )
    if (groupedColumns.length === 0) return

    const trackingKey = `${layerId}:${groupedColumns.length}`
    if (lastTrackedColumnGroupsRef.current === trackingKey) return

    lastTrackedColumnGroupsRef.current = trackingKey
    trackEvent('layers_column_groups_loaded', {
      tool_name: 'data_table',
      layer_id: layerId,
      group_count: groupedColumns.length,
    })
    // `columnDefs` is intentionally omitted — `columnStructureKey` captures
    // the only structural change we care about, and including the raw array
    // would re-fire this effect on every cell-click-triggered mapStyle
    // dispatch (MAM-810).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnStructureKey, layerId, trackEvent])

  const createServerSideDatasource = useCallback(() => {
    const datasource = {
      getRows(params) {
        dispatch(setMapCursor('wait'))

        let processedSortModel = []
        if (params.request.sortModel && params.request.sortModel.length > 0) {
          processedSortModel = params.request.sortModel.filter(sortItem => {
            return !isCoordinateField(sortItem.colId)
          })
        }

        const normalizedFilterModel = normalizeFilterModel(
          params.request.filterModel
        )
        const strippedFilterModel = stripFilterMeta(normalizedFilterModel)
        // The user filters using the displayed imperial values (ft / ft²).
        // Convert any MAM geometry field filter values back to SI (m / m²)
        // so the server compares against its stored raw data correctly.
        const requestFilterModel =
          convertFilterModelMamToSi(strippedFilterModel)

        const rowsRequest = {
          startRow: params.request.startRow || 0,
          endRow: params.request.endRow || 100,
          sortModel: processedSortModel,
          filterModel: requestFilterModel,
        }

        fetchRows(layerId, rowsRequest)
          .then(response => {
            dispatch(setMapCursor('grab'))

            if (!response || !response.data) {
              params.fail()
              dispatch(Alert({ error: 'No layer data on this layer!' }))
              return
            }

            // If sorting resulted in empty data but we requested a sort, retry without sorting
            if (response.count === 0 && processedSortModel.length > 0) {
              const fallbackRequest = {
                ...rowsRequest,
                sortModel: [],
              }
              return fetchRows(layerId, fallbackRequest)
            }

            return response
          })
          .then(response => {
            if (!response) return // Already handled error above

            const rowCountNew = response.count || 0
            const rowData = response.data || []
            setRowCount(rowCountNew)

            const { processedRows, nextOriginalData } = buildRowDataFromServer(
              rowData,
              columnDefs,
              propertyLookupObj
            )
            setOriginalData(nextOriginalData)
            params.success({
              rowData: processedRows,
              rowCount: rowCountNew,
            })
          })
          .catch(error => {
            dispatch(setMapCursor('grab'))
            params.fail()
            const errorMessage = `Failed to fetch data: ${error.message || 'Unknown error'}`
            dispatch(
              Alert({
                error: errorMessage,
              })
            )
          })
      },
    }

    return datasource
  }, [layerId, fetchRows, dispatch, columnDefs, propertyLookupObj])

  const checkForExistingFilter = useCallback(
    gridInstance => {
      if (!mapStyle || !layerId || !gridInstance) return

      const style = getMapStyleJS(mapStyle)
      if (!style || !Array.isArray(style.layers)) return
      const targetLayer = style.layers.filter(layer => layer.id === layerId)

      if (
        targetLayer &&
        targetLayer[0] &&
        targetLayer[0].metadata &&
        targetLayer[0].metadata.filterModel
      ) {
        gridInstance.setFilterModel(targetLayer[0].metadata.filterModel)
        gridInstance.onFilterChanged()
        const filter = gridInstance.getFilterModel()
        const newKey = tableControlKey + 1
        updateNormalizedFilterState(filter)
        setFilterChecked(Boolean(dataTable?.applyFilterToMap))
        setTableControlKey(newKey)
      }
    },
    [dataTable, mapStyle, tableControlKey, layerId, updateNormalizedFilterState]
  )

  const onGridReady = useCallback(
    params => {
      setGridApi(params.api)
      checkForExistingFilter(params.api)
      const datasource = createServerSideDatasource()
      params.api.setGridOption('serverSideDatasource', datasource)
    },
    [createServerSideDatasource, checkForExistingFilter]
  )

  // When the data table is opened with `preSelectMamIds` (e.g. from the
  // Feature Attributes Panel), pre-check those rows so the cyan highlight
  // matches the clicked feature. Server-side rows may not be loaded yet, so
  // we (1) push the geometry to the map selected_features source right away
  // (the cyan highlight does not depend on the row being rendered), and
  // (2) subscribe to `modelUpdated` so the checkbox ticks on when the row
  // arrives in whichever page the user scrolls to. A ref ensures we do the
  // initial GeoJSON fetch exactly once per panel open.
  const preSelectAppliedRef = useRef(false)
  useEffect(() => {
    if (!gridApi) return
    const ids = dataTable?.preSelectMamIds
    if (!ids || ids.length === 0) return

    const idSet = new Set(ids.map(String))

    if (!preSelectAppliedRef.current) {
      getFeatureGeoJson(Array.from(idSet))
      preSelectAppliedRef.current = true
    }

    const markMatchingRowsSelected = () => {
      gridApi.forEachNode(node => {
        const mamid = node.data?.mamid
        if (mamid && idSet.has(String(mamid)) && !node.isSelected()) {
          // third arg = suppressEvents; avoids firing `selectionChanged`
          // which would re-fetch the GeoJSON we just primed.
          node.setSelected(true, false, true)
        }
      })
    }

    markMatchingRowsSelected()
    gridApi.addEventListener('modelUpdated', markMatchingRowsSelected)
    return () => {
      gridApi.removeEventListener?.('modelUpdated', markMatchingRowsSelected)
    }
  }, [gridApi, dataTable?.preSelectMamIds, getFeatureGeoJson])

  // Reset the one-shot flag whenever the data-table config changes (e.g.
  // user closes and reopens the panel for a different feature).
  useEffect(() => {
    preSelectAppliedRef.current = false
  }, [dataTable?.layerId, dataTable?.preSelectMamIds])

  const onFilterChanged = useCallback(() => {
    if (!gridApi) return
    const filter = gridApi.getFilterModel()
    updateNormalizedFilterState(filter)
    setFilterChecked(Boolean(dataTable?.applyFilterToMap))
  }, [gridApi, dataTable?.applyFilterToMap, updateNormalizedFilterState])

  /**
   * Restore column state from localStorage whenever the grid is ready or the
   * column *structure* changes (tracked via `columnStructureKey`, not
   * `columnDefs` itself — see MAM-810). Using the raw `columnDefs` array
   * here caused this effect to re-run on every cell click: row selection
   * dispatches `mapStyle`, which invalidates `tabsConfig`, which gives
   * `columnDefs` a brand-new array reference even though the actual column
   * shape is identical. Re-applying column state on every click produced the
   * visible column-group "jump" the user reported.
   */
  useEffect(() => {
    if (!gridApi || !layerId || !columnStateContextKey) return

    columnStateLifecycle.initialized = false
    columnStateLifecycle.pendingInitialAutoSize = false

    // Restore saved column state when present; otherwise schedule autosizing
    // for the first rendered rows. Measuring the empty startup grid can
    // produce zero/min widths (especially with grouped flex columns), and its
    // resize events must not persist those temporary values.
    let storedColumnState = null
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const columnStateKey = `dataTable_columnState_${persistenceMode}_${layerId}`
        storedColumnState = localStorage.getItem(columnStateKey)
      } catch (error) {
        console.warn('Failed to read column state from localStorage:', error)
      }
    }

    const currentColumnIds = gridApi.getColumnState().map(({ colId }) => colId)
    const columnState = getColumnStateForContext(
      storedColumnState,
      columnStateContextKey,
      currentColumnIds
    )

    if (columnState) {
      try {
        const stateApplied = gridApi.applyColumnState({
          state: columnState,
          applyOrder: true,
        })

        if (!stateApplied) {
          console.warn(
            'Failed to restore column state because its columns no longer match the grid'
          )
          columnStateLifecycle.pendingInitialAutoSize = true
        } else {
          trackEvent('data_table_column_state_restored', {
            tool_name: 'data_table',
            layer_id: layerId,
            column_count: columnState.length,
            persistence_mode: persistenceMode,
          })
          columnStateLifecycle.initialized = true
        }
      } catch (error) {
        console.warn('Failed to restore column state:', error)
        columnStateLifecycle.pendingInitialAutoSize = true
      }
    } else {
      columnStateLifecycle.pendingInitialAutoSize = true
    }

    // A cached server-side response can populate before this React effect
    // runs. Complete the same guarded sizing path immediately instead of
    // waiting for a model-updated event that has already fired.
    onInitialModelUpdated({ api: gridApi })
  }, [
    gridApi,
    columnStateContextKey,
    columnStateLifecycle,
    layerId,
    onInitialModelUpdated,
    persistenceMode,
    trackEvent,
  ])

  const onColumnMoved = useCallback(() => {
    saveColumnState()
  }, [saveColumnState])

  const onColumnResized = useCallback(() => {
    saveColumnState()
  }, [saveColumnState])

  const onColumnVisible = useCallback(() => {
    saveColumnState()
  }, [saveColumnState])

  const onColumnPinned = useCallback(() => {
    saveColumnState()
  }, [saveColumnState])

  const formatValueForExport = useCallback(
    (value, colDef) => {
      if (value === null || value === undefined) return ''

      const fieldType =
        colDef.field === 'mamid'
          ? 'number'
          : propertyLookupObj[colDef.field]?.type

      if (fieldType === 'date' || value instanceof Date) {
        return formatDateForDisplay(value, user)
      }

      // Apply the same SI→imperial conversion used in the cell display so
      // exported values match what the user sees in the table.
      const mamFactor = getMamConversionFactor(colDef.field)
      if (mamFactor != null) {
        const num = parseFloat(value)
        if (!Number.isNaN(num)) return (num * mamFactor).toFixed(3)
      }

      return value
    },
    [propertyLookupObj, user]
  )

  const handleExportAllRows = useCallback(async () => {
    if (!gridApi || !layerId || !columnDefs) {
      dispatch(
        Alert({
          error: 'Data table is not ready for export.',
        })
      )
      return
    }

    // AG-Grid v33: column methods are now on gridApi (columnApi is deprecated)
    const displayedColumns = gridApi.getAllDisplayedColumns?.() || []
    const resolvedColumns = displayedColumns
      .map(column => {
        const colId = column.getColId()
        return columnDefs.find(
          def => def?.colId === colId || def?.field === colId
        )
      })
      .filter(
        col =>
          col && col.field && col.field !== 'zoomTo' && col.field !== 'select'
      )

    if (resolvedColumns.length === 0) {
      dispatch(
        Alert({
          error: 'No visible columns to export.',
        })
      )
      return
    }

    setIsExporting(true)
    trackEvent('data_table_export_started', {
      layer_id: layerId,
      layer_label: layerLabel,
      row_count: rowCount,
    })

    try {
      // Mirror the same normalization pipeline used in getRows() so exported
      // rows always match what is displayed in the grid.
      const normalizedFilterModel = normalizeFilterModel(
        gridApi.getFilterModel?.() || {}
      )
      const strippedFilterModel = stripFilterMeta(normalizedFilterModel)
      const filterModel = convertFilterModelMamToSi(strippedFilterModel)
      // AG-Grid v33: getColumnState is now on gridApi
      const columnState = gridApi.getColumnState?.() || []
      const sortModel = columnState
        .filter(col => {
          if (!col.sort) return false
          const columnId = col.colId || col.field || ''
          return !isCoordinateField(columnId)
        })
        .sort((a, b) => {
          const aIndex = typeof a.sortIndex === 'number' ? a.sortIndex : 0
          const bIndex = typeof b.sortIndex === 'number' ? b.sortIndex : 0
          return aIndex - bIndex
        })
        .map(col => ({
          colId: col.colId || col.field,
          sort: col.sort,
        }))

      const batchSize = 1000
      let startRow = 0
      let totalRows =
        typeof rowCount === 'number' && rowCount >= 0 ? rowCount : null
      const allRows = []
      let shouldContinue = true

      while (shouldContinue) {
        const response = await fetchRows(layerId, {
          startRow,
          endRow: startRow + batchSize,
          filterModel,
          sortModel,
        })

        if (!response || !response.data || response.data.length === 0) {
          break
        }

        const { processedRows } = buildRowDataFromServer(
          response.data,
          columnDefs,
          propertyLookupObj
        )
        allRows.push(...processedRows)

        startRow += response.data.length
        if (typeof totalRows !== 'number') {
          totalRows = response.count ?? startRow
        }

        shouldContinue =
          (typeof totalRows !== 'number' || startRow < totalRows) &&
          response.data.length === batchSize
      }

      if (!allRows.length) {
        throw new Error('No rows available to export.')
      }

      const headers = resolvedColumns.map(
        col => col.headerName || col.field || ''
      )
      const csvLines = [
        headers.map(csvEscapeValue).join(','),
        ...allRows.map(row =>
          resolvedColumns
            .map(col => {
              const formattedValue = formatValueForExport(row[col.field], col)
              return csvEscapeValue(formattedValue)
            })
            .join(',')
        ),
      ]

      const timestamp = new Date().toISOString().split('T')[0]
      const safeLayerLabel = (layerLabel || layerId || 'layer')
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9_-]/g, '')
      const filename = `${safeLayerLabel}-export-${timestamp}.csv`
      downloadCsv(csvLines.join(CSV_NEWLINE), filename)

      dispatch(
        Alert({
          success: `Exported ${allRows.length} row${
            allRows.length === 1 ? '' : 's'
          }.`,
        })
      )
      trackEvent('data_table_export_completed', {
        layer_id: layerId,
        layer_label: layerLabel,
        exported_rows: allRows.length,
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      dispatch(
        Alert({
          error: `Unable to export data: ${message}`,
        })
      )
      trackEvent('data_table_export_failed', {
        layer_id: layerId,
        layer_label: layerLabel,
        error_message: message,
      })
    } finally {
      setIsExporting(false)
    }
  }, [
    gridApi,
    layerId,
    columnDefs,
    dispatch,
    layerLabel,
    rowCount,
    fetchRows,
    propertyLookupObj,
    formatValueForExport,
    trackEvent,
  ])

  const buildSelectedFeatureCollection = useCallback(fetchResults => {
    const newFeatures = []
    fetchResults.forEach(result => {
      if (result && result.data && Array.isArray(result.data)) {
        result.data.forEach(dataItem => {
          if (dataItem && dataItem.data && dataItem.data.features) {
            dataItem.data.features.forEach(feature => {
              newFeatures.push(feature)
            })
          }
        })
      } else if (result && result.data && result.data.features) {
        result.data.features.forEach(feature => {
          newFeatures.push(feature)
        })
      }
    })
    return newFeatures
  }, [])

  const updateSelectLayer = useCallback(
    (results, clearPrevious = false) => {
      // Read mapStyle fresh from the store: this callback is captured into
      // async `featureGeoJsonQuery.mutate` onSuccess handlers, so a render-time
      // closure could be stale by the time it runs. We only read here to decide
      // the highlight geometry; the writes go through atomic actions that
      // read-modify-write the live state in their reducers.
      const currentMapStyle = store.getState().mapStyle
      if (!currentMapStyle?.sources?.selected_features) return

      const layers = currentMapStyle.layers || []
      const targetLayer = layers.find(layer => layer.id === layerId)
      const selectedFeaturesLayer = layers.find(
        layer => layer.id === 'selected_features'
      )
      const selectedFeatureCollection = buildSelectedFeatureCollection(results)

      // Match the highlight layer's geometry (circle vs line) to the target.
      if (selectedFeaturesLayer && targetLayer) {
        const targetType = targetLayer.type
        const currentType = selectedFeaturesLayer.type

        if (
          (targetType === 'circle' && currentType !== 'circle') ||
          (targetType !== 'circle' && currentType !== 'line')
        ) {
          if (targetType === 'circle') {
            const numericRadius = parseFloat(targetLayer.paint['circle-radius'])
            dispatch(
              setLayerType('selected_features', 'circle', {
                'circle-color': SELECTED_FEATURES_LAYER.paint['line-color'],
                'circle-opacity': 0,
                'circle-stroke-color':
                  SELECTED_FEATURES_LAYER.paint['line-color'],
                'circle-stroke-width': 3,
                'circle-radius': !Number.isNaN(numericRadius)
                  ? numericRadius + 1
                  : 5,
              })
            )
          } else {
            dispatch(
              setLayerType(
                'selected_features',
                'line',
                SELECTED_FEATURES_LAYER.paint
              )
            )
          }
        }
      }

      dispatch(setMapCursor('grab'))
      if (clearPrevious) {
        dispatch(setSelectedFeatures(selectedFeatureCollection))
      } else {
        dispatch(appendSelectedFeatures(selectedFeatureCollection))
      }
    },
    [store, layerId, dispatch, buildSelectedFeatureCollection]
  )

  const getFeatureGeoJson = useCallback(
    selectedids => {
      if (!selectedids.length) return

      featureGeoJsonQuery.mutate(
        {
          layerId,
          featureIds: selectedids,
        },
        {
          onSuccess: data => {
            // Selection state may have changed during the FeatureServer
            // fetch: a checkbox toggled off, the FAP DataTable button clicked
            // for a different feature, the table closed and reopened, etc.
            // Filter the response down to features that are STILL part of
            // the user's current intent — the union of visible selected
            // rows AND the active `preSelectMamIds` payload — so a stale
            // onSuccess can't revive a blue highlight the user already
            // dismissed.
            const intendedIds = new Set()
            try {
              gridApi?.getSelectedNodes().forEach(node => {
                if (node.data?.mamid != null) {
                  intendedIds.add(String(node.data.mamid))
                }
              })
            } catch {
              /* gridApi may have been torn down between mutate and onSuccess */
            }
            const preSelected =
              store.getState().dataTable?.preSelectMamIds ?? []
            preSelected.forEach(id => intendedIds.add(String(id)))

            if (intendedIds.size === 0) return

            const filteredChunks = data.map(chunk => ({
              data: {
                features: (chunk.data?.features ?? []).filter(
                  feature =>
                    feature?.properties?.mamid != null &&
                    intendedIds.has(String(feature.properties.mamid))
                ),
              },
            }))
            if (!filteredChunks.some(chunk => chunk.data.features.length > 0)) {
              return
            }

            // Wrap data in expected format for buildSelectedFeatureCollection
            updateSelectLayer([{ data: filteredChunks }])
          },
          onError: () => {
            // Failed to fetch feature geometry
          },
        }
      )
    },
    [layerId, featureGeoJsonQuery, updateSelectLayer, gridApi, store]
  )

  const removeSelectedFeature = useCallback(
    removeIds => {
      // The thunk reads selected_features fresh inside dispatch and removes the
      // matching mamids atomically — no stale-closure risk.
      dispatch(removeSelectedFeatures(removeIds))
    },
    [dispatch]
  )

  const onRowSelected = useCallback(
    params => {
      if (!params.node.selected) {
        removeSelectedFeature([params.data.mamid])
      }
    },
    [removeSelectedFeature]
  )

  const selectionChanged = useCallback(() => {
    if (isZoomToFeatureRef.current) {
      isZoomToFeatureRef.current = false
      return
    }

    if (!gridApi) return

    // Fresh read (see the `store` comment near the top of the component). The
    // closure mapStyle here would skew the "already selected" diff calculation
    // — particularly during a cold DataTable mount where multiple dispatches
    // race against AG-Grid's `selectionChanged` firing from
    // `markMatchingRowsSelected`. Using the live state keeps the diff aligned
    // with what's actually in `selected_features`.
    const currentMapStyle = store.getState().mapStyle
    if (!currentMapStyle) return

    const style = getMapStyleJS(currentMapStyle)
    if (!style) return
    const selectedNodes = gridApi.getSelectedNodes()
    const selectedFeatures =
      style.sources?.selected_features?.data?.features || []
    const selectedIds = selectedNodes
      .filter(node => {
        if (!node.data?.mamid) return false

        const alreadySelected = selectedFeatures.filter(
          feature => feature?.properties?.mamid === node.data.mamid
        )
        if (!alreadySelected[0]) return true
        return false
      })
      .map(node => node.data.mamid)

    getFeatureGeoJson(selectedIds)
  }, [gridApi, store, getFeatureGeoJson])

  const zoomToFeature = useCallback(
    featureId => {
      dispatch(setMapCursor('wait'))

      featureBoundsQuery.mutate(
        {
          layerId,
          featureId,
        },
        {
          onSuccess: data => {
            dispatch(setMapCursor('grab'))

            if (!data.features || !data.features[0]) {
              dispatch(Alert({ error: 'Feature not found or has no geometry' }))
              return
            }

            const feature = data.features[0]

            // Support multiple geometry types, not just Polygon
            const supportedTypes = [
              'Polygon',
              'MultiPolygon',
              'LineString',
              'MultiLineString',
              'Point',
              'MultiPoint',
            ]
            if (!supportedTypes.includes(feature.geometry.type)) {
              dispatch(
                Alert({
                  error: `Geometry type ${feature.geometry.type} not supported for zoom`,
                })
              )
              return
            }

            try {
              const bounds = getBBox(feature)

              // Validate bounds array
              if (
                !bounds ||
                bounds.length !== 4 ||
                bounds.some(b => typeof b !== 'number' || Number.isNaN(b))
              ) {
                dispatch(
                  Alert({ error: 'Invalid bounds calculated for feature' })
                )
                return
              }

              const formattedBounds = [
                [bounds[0], bounds[1]], // [minLng, minLat]
                [bounds[2], bounds[3]], // [maxLng, maxLat]
              ]

              // Set flag to prevent selection interference
              isZoomToFeatureRef.current = true

              // Temporarily disable LabelFeatures during zoom to prevent layer visibility issues
              // LabelFeatures rebuilds the layer array during viewport changes which can cause
              // hidden layers to temporarily appear visible during the zoom transition
              dispatch(setZoomingToFeature(true))

              // Zoom first for immediate feedback
              zoomToBounds(formattedBounds)

              // Then highlight the feature by directly updating the map style
              featureGeoJsonQuery.mutate(
                {
                  layerId,
                  featureIds: [featureId],
                },
                {
                  onSuccess: geoJsonData => {
                    // Use clearPrevious=true to replace all existing selections
                    // Wrap data in expected format for buildSelectedFeatureCollection
                    updateSelectLayer([{ data: geoJsonData }], true)

                    // Re-enable LabelFeatures after highlighting is complete
                    // Small delay ensures the highlighting operation finishes before LabelFeatures resumes
                    setTimeout(() => {
                      dispatch(setZoomingToFeature(false))
                    }, 100)
                  },
                  onError: () => {
                    // Re-enable LabelFeatures even on error
                    dispatch(setZoomingToFeature(false))
                  },
                }
              )
            } catch (error) {
              console.error('Failed to calculate bounds for feature:', error)
            }
          },
          onError: error => {
            dispatch(setMapCursor('grab'))
            console.error('Failed to fetch feature geometry:', error)
          },
        }
      )
    },
    [
      layerId,
      featureBoundsQuery,
      zoomToBounds,
      dispatch,
      featureGeoJsonQuery,
      updateSelectLayer,
    ]
  )

  /**
   * Normalizes values for accurate comparison using consolidated utility
   */
  const normalizeValue = useCallback((value, fieldType) => {
    if (value === null || value === undefined || value === '') {
      return null
    }

    // Handle date formatting using consolidated utility
    if (fieldType === 'date') {
      return toDateObject(value)
    }

    // Handle number formatting - ensure consistent type
    if (fieldType === 'number' || fieldType === 'currency') {
      const numValue = Number(value)
      return Number.isNaN(numValue) ? value : numValue
    }

    // For other types, ensure string consistency
    return String(value)
  }, [])

  /**
   * Handles cell value changes and tracks edits locally
   * @param {Object} params - AG-Grid cell change parameters
   */
  const onCellValueChanged = useCallback(
    params => {
      const { data, colDef, oldValue, newValue } = params
      const { mamid: featureID } = data
      const { field } = colDef

      // Get field type for proper comparison
      const fieldType = propertyLookupObj[field]?.type || 'text'

      // Normalize both values for accurate comparison
      const normalizedOldValue = normalizeValue(oldValue, fieldType)
      const normalizedNewValue = normalizeValue(newValue, fieldType)

      // No need to update cell data manually - valueFormatter handles display formatting

      // Only process if there's an actual change
      if (featureID && field && normalizedOldValue !== normalizedNewValue) {
        // Store the change locally for batch processing
        setEditedCells(prev => {
          const updated = { ...prev }
          if (!updated[featureID]) {
            updated[featureID] = {}
          }

          // Get original value for comparison
          const featureData = originalData[featureID]
          const originalValue = featureData ? featureData[field] : oldValue
          const normalizedOriginalValue = normalizeValue(
            originalValue,
            fieldType
          )

          // If reverting to original value, remove from edited cells
          if (normalizedNewValue === normalizedOriginalValue) {
            delete updated[featureID][field]
            // If no more edits for this feature, remove the feature entry
            if (Object.keys(updated[featureID]).length === 0) {
              delete updated[featureID]
            }
          } else {
            // Track the edit with normalized value
            updated[featureID][field] = {
              newValue: normalizedNewValue,
              oldValue: normalizedOriginalValue,
            }
          }

          return updated
        })
      }
    },
    [normalizeValue, originalData, propertyLookupObj]
  )

  /**
   * Update unsaved changes flag and refresh cell styling when edits change
   */
  useEffect(() => {
    const hasEdits = Object.keys(editedCells).length > 0
    setHasUnsavedChanges(hasEdits)

    if (gridApi) {
      // Force refresh all cells to apply new styling
      gridApi.refreshCells({
        force: true,
        suppressFlash: true,
      })

      // Backup approach: directly apply styles to DOM elements
      // This ensures styling works even if AG-Grid callbacks don't fire
      setTimeout(() => {
        const allCells = document.querySelectorAll('.ag-cell')
        allCells.forEach(cell => {
          const cellData = cell.getAttribute('col-id')
          const rowData = cell.closest('.ag-row')?.getAttribute('row-id')

          if (
            cellData &&
            rowData &&
            editedCells[rowData] &&
            editedCells[rowData][cellData]
          ) {
            cell.style.backgroundColor = '#FCD857'
            cell.style.border = '1px solid #e6c23d'
            cell.style.boxSizing = 'border-box'
            cell.classList.add('ag-cell-edited')
          } else if (cell.classList.contains('ag-cell-edited')) {
            // Remove styling if cell is no longer edited
            cell.style.backgroundColor = ''
            cell.style.border = ''
            cell.style.boxSizing = ''
            cell.classList.remove('ag-cell-edited')
          }
        })
      }, 100)
    }
  }, [editedCells, gridApi])

  // Global refresh listener (triggered by useLayerRowsRefresher)
  useEffect(() => {
    const handler = e => {
      const detailLayerId = e?.detail?.layerId
      if (!gridApi) return
      if (!layerId || (detailLayerId && detailLayerId !== layerId)) return
      gridApi.refreshServerSide()
    }
    window.addEventListener('datatable:refresh', handler)
    return () => window.removeEventListener('datatable:refresh', handler)
  }, [gridApi, layerId])

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
        },
      ],
    }),
    []
  )

  const containerStyle = useMemo(
    () => ({
      height: height ? `${height}px` : '100%',
      width: '100%',
    }),
    [height]
  )

  const ZoomToFeatureButtonWrapper = useCallback(
    props => <ZoomToFeatureButton {...props} onZoomToFeature={zoomToFeature} />,
    [zoomToFeature]
  )

  const components = useMemo(
    () => ({
      zoomToFeatureButton: ZoomToFeatureButtonWrapper,
    }),
    [ZoomToFeatureButtonWrapper]
  )

  if (propertiesError) {
    return (
      <div className={scss.dataTableWrapper} style={containerStyle}>
        <div className={scss.tableContainer}>
          <div>Error loading layer properties: {propertiesError.message}</div>
        </div>
      </div>
    )
  }

  if (!Array.isArray(layerProperties) && layerProperties) {
    return (
      <div className={scss.dataTableWrapper} style={containerStyle}>
        <div className={scss.tableContainer}>
          <div>Invalid layer properties format</div>
        </div>
      </div>
    )
  }

  if (!layerId) {
    return (
      <div className={scss.dataTableWrapper} style={containerStyle}>
        <div className={scss.tableContainer}>
          <div>No layer selected</div>
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyle} className={scss.dataTableWrapper}>
      <TableControls
        key={tableControlKey}
        fetching={layerRowsFetching}
        rowCount={rowCount}
        gridApi={gridApi}
        currentFilter={currentFilter}
        filterChecked={filterChecked}
        onClose={onClose}
        hasUnsavedChanges={hasUnsavedChanges}
        onSaveChanges={handleSaveChanges}
        onCancelChanges={handleCancelChanges}
        isSaving={
          updateFeatureMutation.isPending || bulkUpdateMutation.isPending
        }
        onExport={handleExportAllRows}
        exporting={isExporting}
        tabsEnabled={tabsEnabled}
        onToggleTabs={handleToggleTabs}
      />
      <div className={scss.tableContainer}>
        {columnDefs && (
          <div
            style={{ height: '100%', width: '100%' }}
            data-testid='data-table-grid'
          >
            <AgGridReact
              theme={dataTableTheme}
              onGridReady={onGridReady}
              onModelUpdated={onInitialModelUpdated}
              onFilterChanged={onFilterChanged}
              onCellValueChanged={onCellValueChanged}
              onCellEditingStopped={e =>
                onCellValueChanged({
                  ...e,
                  oldValue:
                    (originalData &&
                      originalData[String(e.data?.mamid)] &&
                      originalData[String(e.data?.mamid)][e.colDef?.field]) ??
                    undefined,
                  newValue: e.value,
                })
              }
              onRowSelected={onRowSelected}
              onSelectionChanged={selectionChanged}
              onColumnMoved={onColumnMoved}
              onColumnResized={onColumnResized}
              onColumnVisible={onColumnVisible}
              onColumnPinned={onColumnPinned}
              onColumnGroupOpened={autoSizeExpandedColumnGroups}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              components={components}
              stopEditingWhenCellsLoseFocus={true}
              suppressColumnVirtualisation={false}
              selectionColumnDef={{ width: 58 }}
              rowSelection={{
                mode: 'multiRow',
                checkboxes: true,
                // Selection is checkbox-only. With `enableClickSelection: true`
                // (AG-Grid default for multiRow without modifier keys), a click
                // on the row body — even just outside the visible checkbox —
                // clears every other selection and selects only that row. That
                // confuses users who expect the checkboxes to be the single
                // source of truth for selection, and it triggers a cascade of
                // `onRowSelected`/`selectionChanged` events that race against
                // any in-flight FeatureServer mutations (revived blue
                // highlights on rows the user thinks they just dismissed).
                enableClickSelection: false,
              }}
              cellSelection={{
                handle: {
                  mode: 'fill',
                },
              }}
              rowHeight={22}
              sideBar={sideBar}
              pivotMode={false}
              rowModelType='serverSide'
              pagination={false}
              cacheBlockSize={cacheBlockSize}
              blockLoadDebounceMillis={1000}
              animateRows
              paginateChildRows
              localeTextFunc={localeTextFunc}
              getRowId={params => String(params.data.mamid)}
            />
          </div>
        )}
        {!columnDefs && propertiesLoading && (
          <div>Loading layer properties...</div>
        )}
        {!columnDefs && !propertiesLoading && <div>No data available</div>}
      </div>
    </div>
  )
})

DataTable.displayName = 'DataTable'

export default DataTable
